import { Link, Form, useModal } from "@npco/zeller-design-system";
import { bindField, useForm } from "@npco/zeller-design-system/form";
import * as Input from "@npco/zeller-design-system/input";
import type { ADT } from "pb.adt";
import { useCallback, useState } from "react";
import { twJoin, twMerge } from "tailwind-merge";
import { z } from "zod";

import { logger } from "../../../shared/logger";
import { Layout } from "../shared/layout";
import type { Page } from "../terminal-setup";
import { TerminalPreview } from "./page-paired/terminal-preview";

const DeviceNameValuesSchema = z.object({
	deviceName: z.string(),
});

export function PagePaired({
	page: {
		deviceName: deviceNameInitial,
		terminalName,
		terminalModel,
		terminalColor,
		terminalLogoUrl,
		onRename,
		onResetDevice,
		onChangePairing,
	},
	onClose,
	onLogout,
	pending: _pending,
}: {
	page: ADT.Pick<Page, "Paired">;
} & Required<Pick<Layout.Props, "onClose" | "onLogout" | "pending">>) {
	const modal = useModal({
		variant: "decision",
		title: "Reset Device",
		content: "Are you sure you want to reset this device?",
		primary: {
			label: "Reset",
			onClick: () => {
				setPending("reset");
				return onResetDevice();
			},
		},
		destructive: true,
		overlayBlur: true,
	});

	const form = useForm(
		DeviceNameValuesSchema,
		async ({ value: { deviceName }, formApi }) => {
			const response = await onRename(deviceName);
			if (response instanceof Error) {
				logger.error("setup: saving rename device error", {
					errorName: response.name,
					errorMessage: response.message,
					errorStack: response.stack,
				});
				return formApi.setErrorMap({
					onSubmit: "Failed." as unknown as undefined,
				});
			}
			formApi.reset();
			setRenaming(false);
		},
	);

	// Merge "reset" "rename" and form submitting pending states into one.
	const [$pending, setPending] = useState<"reset">();
	const [renaming, setRenaming] = useState(false);
	const onClickRename = useCallback(() => {
		form.setFieldValue("deviceName", deviceNameInitial);
		setRenaming(true);
	}, [form, deviceNameInitial]);
	const onClickRenameCancel = useCallback(() => {
		form.reset();
		setRenaming(false);
	}, [form]);

	return (
		<form.Subscribe selector={(state) => state.isSubmitting}>
			{(isSubmitting) => {
				const pending = _pending || (isSubmitting ? "renaming" : $pending);
				return (
					<Layout
						title="Device Details"
						onClose={onClose}
						onLogout={onLogout}
						pending={pending === "logout" ? pending : !!pending}
					>
						{modal.element}
						<div className="flex px-56 pt-6 transition-all">
							<TerminalPreview
								className={twMerge(
									"hidden [@media(min-height:550px)]:md:block",
									"transition-all",
									"pt-20 [@media(min-height:625px)]:pt-64",
								)}
								model={terminalModel}
								terminalColour={terminalColor}
								terminalLogoUrl={terminalLogoUrl}
							/>
							<div className="flex flex-1 flex-col gap-40">
								<section className="flex flex-col gap-24">
									<Form onSubmit={form.handleSubmit}>
										<form.Field
											name="deviceName"
											children={(field) =>
												!renaming ? (
													<div
														className={twJoin(
															"h-[56px]",
															"flex flex-row items-center justify-between",
															"border-b",
															"border-grey-90",
															"dark:border-grey-800",
														)}
													>
														<div className="text-lg/heading">
															{deviceNameInitial}
														</div>
														<div>
															<Link
																label="Rename"
																onClick={onClickRename}
																disabled={!!pending}
															/>
														</div>
													</div>
												) : (
													<div className="h-[56px]">
														<Input.Text
															{...bindField(field)}
															label="POS Register Name"
															error={!!field.form.state.errors.length}
															disabled={isSubmitting}
															actions={[
																{
																	label: "Save",
																	onClick: () => undefined,
																	disabled: !!pending,
																	pending: pending === "renaming",
																	$button: { type: "submit" },
																},
																{
																	label: "Cancel",
																	onClick: onClickRenameCancel,
																	disabled: !!pending,
																},
															]}
														/>
													</div>
												)
											}
										/>
									</Form>
									<div className="text-base flex flex-col gap-16">
										<div className="flex h-[24px] flex-row items-center justify-between gap-16">
											<div>Zeller Terminal Name</div>
											<div className="text-right text-md/heading">
												{terminalName}
											</div>
										</div>
									</div>
								</section>
								<div className="flex flex-col justify-start gap-24">
									<Link
										label="Change Terminal Pairing"
										onClick={onChangePairing}
										disabled={!!pending}
									/>
									<Link
										label="Reset Device"
										destructive
										disabled={!!pending}
										onClick={() => {
											modal.open();
										}}
									/>
								</div>
							</div>
						</div>
					</Layout>
				);
			}}
		</form.Subscribe>
	);
}
